import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useGraphEditorStore } from '@/graph-store';

export interface AddCycleHereDialogProps {
  groupId: string;
  anchorOpId?: string;
  onClose: () => void;
}

export function AddCycleHereDialog({ groupId, anchorOpId, onClose }: AddCycleHereDialogProps) {
  const group = useGraphEditorStore((s) => s.graphData.groups[groupId]);
  const operationNodes = useGraphEditorStore((s) => s.graphData.operationNodes);
  const addCycle = useGraphEditorStore((s) => s.addCycle);

  const anchorIndex = useMemo(() => {
    if (!group || !anchorOpId) return 0;
    const idx = group.stepIds.indexOf(anchorOpId);
    return idx >= 0 ? idx : 0;
  }, [group, anchorOpId]);

  const [startIndex, setStartIndex] = useState(anchorIndex);
  const [endIndex, setEndIndex] = useState(anchorIndex);
  const [iterationCount, setIterationCount] = useState(2);
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!group) return null;

  const maxIdx = group.stepIds.length - 1;

  // Build step options: display "1. StepName" but bind to index
  const stepOptions = group.stepIds.map((opId, index) => {
    const op = operationNodes[opId];
    return {
      value: index,
      label: `${index + 1}. ${op?.displayName ?? opId}`,
    };
  });

  const previewSteps = useMemo(() => {
    if (startIndex < 0 || endIndex < startIndex || endIndex > maxIdx) return [];
    return group.stepIds.slice(startIndex, endIndex + 1).map(
      (id) => operationNodes[id]?.displayName ?? id,
    );
  }, [startIndex, endIndex, maxIdx, group.stepIds, operationNodes]);

  const handleSave = () => {
    setError(null);
    if (startIndex > endIndex) {
      setError('Start step must come before end step.');
      return;
    }
    try {
      addCycle(groupId, {
        startIndex,
        endIndex,
        iterationCount,
        label: label || undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add Cycle</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Group: <strong>{group.label}</strong> ({group.stepIds.length} steps)
          </Typography>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              select
              label="Start step"
              size="small"
              value={startIndex}
              onChange={(e) => setStartIndex(Number(e.target.value))}
              sx={{ flex: 1 }}
            >
              {stepOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="End step"
              size="small"
              value={endIndex}
              onChange={(e) => setEndIndex(Number(e.target.value))}
              sx={{ flex: 1 }}
            >
              {stepOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <TextField
            label="Iterations"
            type="number"
            size="small"
            fullWidth
            value={iterationCount}
            onChange={(e) => setIterationCount(Number(e.target.value))}
            slotProps={{ htmlInput: { min: 2 } }}
          />

          <TextField
            label="Label (optional)"
            size="small"
            fullWidth
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />

          {previewSteps.length > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Steps in cycle:
              </Typography>
              {previewSteps.map((name, i) => (
                <Typography key={i} variant="body2">
                  &bull; {name}
                </Typography>
              ))}
            </Box>
          )}

          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={
            startIndex < 0 ||
            endIndex < startIndex ||
            endIndex > maxIdx ||
            iterationCount < 2
          }
          onClick={handleSave}
        >
          Add Cycle
        </Button>
      </DialogActions>
    </Dialog>
  );
}
